/** Clave por defecto de {@link createWebStorageTokenStorage}. */
export const DEFAULT_SESSION_KEY = 'fitness.session';
/**
 * Convierte el par de tokens que devuelve la API en una {@link Session}, pasando
 * `expiresIn` (segundos restantes) a un instante absoluto.
 */
export function sessionFromTokenPair(tokens, now = Date.now()) {
    return {
        accessToken: tokens.accessToken,
        ...(tokens.refreshToken ? { refreshToken: tokens.refreshToken } : {}),
        expiresAt: now + tokens.expiresIn * 1000,
    };
}
/** Comprueba que un valor arbitrario tiene la forma de una {@link Session}. */
export function isSession(value) {
    if (typeof value !== 'object' || value === null)
        return false;
    const candidate = value;
    return (typeof candidate['accessToken'] === 'string' &&
        (candidate['refreshToken'] === undefined || typeof candidate['refreshToken'] === 'string') &&
        typeof candidate['expiresAt'] === 'number' &&
        Number.isFinite(candidate['expiresAt']));
}
/** Almacén en memoria: útil en tests, scripts y renderizado en servidor. */
export class MemoryTokenStorage {
    #session;
    constructor(initial = null) {
        this.#session = initial;
    }
    get() {
        return Promise.resolve(this.#session);
    }
    set(session) {
        this.#session = session;
        return Promise.resolve();
    }
    clear() {
        this.#session = null;
        return Promise.resolve();
    }
}
/**
 * Almacén sobre `localStorage` / `sessionStorage`.
 *
 * Tolera que el navegador deniegue el acceso (modo privado, cookies bloqueadas):
 * en ese caso se comporta como si no hubiera sesión en lugar de lanzar. Un valor
 * corrupto se descarta y se limpia.
 *
 * @example
 * const storage = createWebStorageTokenStorage(window.localStorage);
 */
export function createWebStorageTokenStorage(storage, key = DEFAULT_SESSION_KEY) {
    return {
        get() {
            let raw = null;
            try {
                raw = storage.getItem(key);
            }
            catch {
                return Promise.resolve(null);
            }
            if (!raw)
                return Promise.resolve(null);
            try {
                const parsed = JSON.parse(raw);
                if (isSession(parsed))
                    return Promise.resolve(parsed);
            }
            catch {
                // Valor ilegible: cae al borrado de abajo.
            }
            try {
                storage.removeItem(key);
            }
            catch {
                // Nada que hacer si el almacén tampoco deja borrar.
            }
            return Promise.resolve(null);
        },
        set(session) {
            try {
                storage.setItem(key, JSON.stringify(session));
            }
            catch {
                // Cuota llena o acceso denegado: la sesión sigue viva en memoria.
            }
            return Promise.resolve();
        },
        clear() {
            try {
                storage.removeItem(key);
            }
            catch {
                // Ídem.
            }
            return Promise.resolve();
        },
    };
}
//# sourceMappingURL=session.js.map